import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  TableRestaurant,
  People,
  CheckCircle,
  Schedule,
} from "@mui/icons-material";

const TableCard = ({ table, status, statusInfo, activeOrder, onClick }) => {
  const theme = useTheme();

  // Define status colors
  const statusColors = {
    available: {
      main: "#10b981",
      dark: "#059669",
    },
    occupied: {
      main: "#f59e0b",
      dark: "#d97706",
    },
    reserved: {
      main: "#f59e0b",
      dark: "#d97706",
    },
  };

  const currentColor = statusColors[status] || statusColors.available;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        height: "100%",
        minHeight: 180,
        bgcolor: alpha(currentColor.main, 0.03),
        border: `1px solid ${alpha(currentColor.main, 0.15)}`,
        borderRadius: "12px",
        transition: "all 0.2s ease",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 20px ${alpha(currentColor.main, 0.2)}`,
          borderColor: alpha(currentColor.main, 0.3),
        },
      }}
    >
      <CardContent
        sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Header: Table Number & Status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${currentColor.main}, ${currentColor.dark})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${alpha(currentColor.main, 0.3)}`,
              }}
            >
              <TableRestaurant sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  lineHeight: 1.2,
                }}
              >
                #{table.tableNumber}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.85),
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                {table.type}
              </Typography>
            </Box>
          </Box>

          {/* Status Chip */}
          <Chip
            label={statusInfo.text}
            size="small"
            icon={status === "available" ? <CheckCircle /> : <Schedule />}
            sx={{
              bgcolor: alpha(currentColor.main, 0.15),
              color: currentColor.dark,
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 26,
              borderRadius: "8px",
              border: `1px solid ${alpha(currentColor.main, 0.2)}`,
              "& .MuiChip-icon": {
                color: currentColor.main,
                fontSize: 14,
              },
            }}
          />
        </Box>

        {/* Capacity */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1.5,
            px: 1.5,
            bgcolor: alpha(theme.palette.grey[500], 0.06),
            borderRadius: "10px",
            mb: activeOrder ? 1.5 : 0,
          }}
        >
          <People
            sx={{
              fontSize: 18,
              color: theme.palette.text.secondary,
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, fontSize: "0.85rem" }}
          >
            {table.capacity} people
          </Typography>
        </Box>

        {/* Active Order Info */}
        {activeOrder && (
          <Box
            sx={{
              mt: "auto",
              pt: 1.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: currentColor.main,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: currentColor.main,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Active Order
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.9rem",
                }}
              >
                #{activeOrder.orderNumber}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  background: `linear-gradient(135deg, ${currentColor.main}, ${currentColor.dark})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Rs. {parseFloat(activeOrder.totalAmount).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TableCard;
